import type {
  AskAvailableResult,
  AskConversationSnapshot,
  AskResult,
  AskTurn,
  AskTurnStatus,
} from '@/services/ask/api';

/**
 * Reads over a conversation snapshot. They take the snapshot, not store state,
 * because the snapshot is the only place turns live.
 *
 * `stalled` is not active: the server reports it for a turn no worker finished
 * in time, it accepts a new turn past it, and the reader must be able to retry.
 */

const ACTIVE_STATUSES: ReadonlySet<AskTurnStatus> = new Set(['queued', 'running']);

const UNFINISHED_STATUSES: ReadonlySet<AskTurnStatus> = new Set(['failed', 'stalled']);

export function isActiveTurn(turn: Pick<AskTurn, 'status'>): boolean {
  return ACTIVE_STATUSES.has(turn.status);
}

/** A turn that ended without results and offers Retry. */
export function isUnfinishedTurn(turn: Pick<AskTurn, 'status'>): boolean {
  return UNFINISHED_STATUSES.has(turn.status);
}

/**
 * Whether a failed or stalled turn still offers Retry: only while it is the
 * conversation's last turn. Once anything follows it, whether its own retry or
 * a different question, asking it again would append the same question as a
 * new follow-up and run a second search for something already answered or
 * moved past.
 */
export function isRetryableTurn(
  snapshot: AskConversationSnapshot | undefined,
  turn: Pick<AskTurn, 'id' | 'status'>
): boolean {
  return isUnfinishedTurn(turn) && snapshot?.turns.at(-1)?.id === turn.id;
}

export function selectActiveTurn(snapshot: AskConversationSnapshot | undefined): AskTurn | null {
  return snapshot?.turns.find(isActiveTurn) ?? null;
}

export function hasActiveTurn(snapshot: AskConversationSnapshot | undefined): boolean {
  return selectActiveTurn(snapshot) != null;
}

/**
 * The turn a follow-up builds on. The server's `latestTurnId` is written only
 * when a turn completes, so it can never name a failed turn; this reads it
 * rather than guessing from order, since a retry shares its failed sibling's
 * `turnIndex`.
 */
export function selectLatestCompletedTurn(
  snapshot: AskConversationSnapshot | undefined
): AskTurn | null {
  if (!snapshot?.latestTurnId) {
    return null;
  }

  return snapshot.turns.find((turn) => turn.id === snapshot.latestTurnId) ?? null;
}

/**
 * The turn "Start an inquiry" hands to an expert: the latest completed turn,
 * but only while it is also the conversation's last. A follow-up that is still
 * running, or that failed or stalled, would otherwise hand off the question
 * before it and silently drop what the reader just added.
 */
export function selectHandoffTurn(snapshot: AskConversationSnapshot | undefined): AskTurn | null {
  const latest = selectLatestCompletedTurn(snapshot);

  return latest && snapshot?.turns.at(-1)?.id === latest.id ? latest : null;
}

/** An earlier turn in this conversation already showed this result. */
export function isRepeatResult(result: AskResult, turnId: string): boolean {
  return !result.unavailable && result.firstSeenTurnId !== turnId;
}

/**
 * The result a selection points at, or null when the snapshot no longer holds
 * it available: the turn is gone, or its question has lost every post since.
 */
export function selectSelectedResult(
  snapshot: AskConversationSnapshot | undefined,
  selection: { turnId: string; source: string; resultId: string } | null
): AskAvailableResult | null {
  if (!snapshot || !selection) {
    return null;
  }

  const result = snapshot.turns
    .find((turn) => turn.id === selection.turnId)
    ?.results.find(
      (candidate) =>
        candidate.source === selection.source && candidate.resultId === selection.resultId
    );

  return result && !result.unavailable ? result : null;
}

/** A result a reader can open, with its place among all the turn's results. */
export type NavigableResult = { result: AskAvailableResult; position: number };

/**
 * The results on a turn that the result view steps between: available
 * inquiries, in ranked order. `position` is the result's index among all the
 * turn's results, which is what result analytics report, so it can skip
 * numbers where an unavailable or non-inquiry result sits between two.
 */
export function selectNavigableResults(
  snapshot: AskConversationSnapshot | undefined,
  turnId: string
): NavigableResult[] {
  const turn = snapshot?.turns.find((candidate) => candidate.id === turnId);

  if (!turn) {
    return [];
  }

  return turn.results.flatMap((result, position) =>
    !result.unavailable && result.source === 'inquiry' ? [{ result, position }] : []
  );
}
